import { prop, pathOr } from 'ramda';
import { createSelector } from 'reselect';

import { EvoPlayerConfig, NanoPlayerConfig } from '@ezugi/types';
import { DeviceInfo } from '@ezugi/utils';

import { RootState } from '../types';
import { isEvoPlayerSelector } from './config';

export const videoSelector = (state: RootState) => state.video;

export const videoLoadingSelector = createSelector(
  videoSelector,
  (video) => video.loading
);

export const videoDestroyedSelector = createSelector(
  videoSelector,
  (video) => video.destroyed
);

export const videoErrorSelector = createSelector(
  videoSelector,
  (video) => video.error
);

export const videoPauseSelector = createSelector(
  videoSelector,
  (video) => video.pause
);

export const videoCDNListSelector = createSelector(
  videoSelector,
  (video) => video.cdnList
);

export const videoConfigLoadedSelector = createSelector(
  videoSelector,
  (video) => video.getStreamsConfigLoaded
);

export const evoPlayerExtraConfigSelector = createSelector(
  isEvoPlayerSelector,
  videoCDNListSelector,
  (isEvoPlayer, config) => {
    try {
      return isEvoPlayer ? JSON.parse((config as EvoPlayerConfig)?.extraConfig) : {};
    } catch {
      return {};
    }
  }
);

export const videoContainerMountedSelector = createSelector(
  videoSelector,
  prop('evoContainerMounted')
);

export const videoCurrentAngleSelector = createSelector(
  videoSelector,
  prop('angle')
);

/** camera angle inside a stream (when angle can change inside the same stream) */
export const videoAngleInStreamSelector = createSelector(
  videoSelector,
  (video) => video.angleInStream
);

export const selectedStreamQualitySelector = createSelector(
  videoSelector,
  prop('selectedStreamQuality')
);

export const videoStreamsEntriesSelector = createSelector(
  isEvoPlayerSelector,
  videoCDNListSelector,
  videoCurrentAngleSelector,
  (isEvoPlayer, cdnList, angle) => {
    const streams = (!isEvoPlayer && cdnList) ? (cdnList as NanoPlayerConfig)[angle] : [];
    return streams;
  }
);

export const videoStreamsStartIndexSelector = createSelector(
  videoStreamsEntriesSelector,
  (entries = []) => {
    const { index } = entries.find(
      (_e) => _e.defaultPlatform === (DeviceInfo.isHandheld ? 'mobile' : 'desktop')
    ) || { index: 0 };

    return index;
  }
);

export const videoSelectedStreamSelector = createSelector(
  videoStreamsEntriesSelector,
  videoStreamsStartIndexSelector,
  (streams, index) => pathOr(null, [ index ], streams)
);

export const videoIsSecureSelector = createSelector(
  isEvoPlayerSelector,
  videoCDNListSelector,
  (isEvoPlayer, videoCDN) => (isEvoPlayer ? true : (videoCDN as NanoPlayerConfig).secure)
);

export const videoReconnectSelector = createSelector(
  videoSelector,
  (video) => video.reconnect ?? 0
);

export const videoDelaySelector = createSelector(
  videoCDNListSelector,
  (videoCDN) => videoCDN?.videoDelay ?? '0' as unknown
);

export const videoIsPlayingSelector = createSelector(
  videoSelector,
  prop('isPlaying')
);

export const videoReadyStatusSelector = createSelector(
  videoSelector,
  prop('ready')
);

export const videoCurrentPlayerSelector = createSelector(
  videoSelector,
  prop('currentPlayer')
);

export const videoFullWidthSelector = createSelector(
  videoSelector,
  (video) => video.fullWidth
);

export const videoAvailableQualitiesSelector = createSelector(
  videoSelector,
  (video) => video.availableQualities
);

export const nanoPlayerLoadedSelector = createSelector(
  videoSelector,
  (video) => video.nanoLoaded
);

export const videoPlayingSelector = createSelector(
  videoLoadingSelector,
  videoReadyStatusSelector,
  videoDestroyedSelector,
  videoErrorSelector,
  videoPauseSelector,
  isEvoPlayerSelector,
  (loading, ready, destroyed, error, pause, isEvoPlayer) => (isEvoPlayer
    ? ready
    : !destroyed
        && ready
        && !loading
        && typeof error !== 'undefined'
        && typeof pause !== 'undefined')
);

export const nanoPlayerPendingReconnectSelector = createSelector(
  videoSelector,
  (video) => video.nanoPlayerPendingReconnect
);
